/**
 * FormDatePicker / FormDateRangePicker — antd DatePicker 래퍼 (i18n 프리셋 + allowClear).
 *
 * 【역할】
 * - 단일 날짜 선택 (FormDatePicker) 과 기간 선택 (FormDateRangePicker) 을 모두 제공.
 * - RangePicker 에 i18n 프리셋(Today/Yesterday/Last 7/Last 30/This month/Last month) 자동 주입.
 *
 * 【주요 기능】
 * - allowClear 기본 ON.
 * - RangePicker presets 미지정 시 useLabel() 기반 6개 프리셋 자동 주입.
 * - V020 시드: LBL_PRESET_TODAY/YESTERDAY/LAST_7/LAST_30/THIS_MONTH/LAST_MONTH × 5 locale.
 *
 * 【Props】
 * - FormDatePicker: antd DatePickerProps 그대로 — allowClear 기본 true.
 * - FormDateRangePicker: antd RangePickerProps 그대로 — allowClear/presets 기본값 덮어쓰기.
 *
 * 【사용 예시】
 * ```tsx
 * <FormDatePicker value={d} onChange={setD} />
 * <FormDateRangePicker onChange={(range) => console.log(range)} />
 * ```
 */
import { useMemo } from 'react'
import { DatePicker } from 'antd'
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import { useLabel } from '@/framework/useLabel'

/**
 * DatePicker 프리셋 — locale 에 맞춰 동적 라벨 생성.
 * V020 시드: LBL_PRESET_TODAY/YESTERDAY/LAST_7/LAST_30/THIS_MONTH/LAST_MONTH × 5 locale.
 */
function usePresets(): { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] {
  const { t } = useLabel()
  return useMemo(
    () => [
      {
        label: t('LBL_PRESET_TODAY', 'Today'),
        value: [dayjs().startOf('day'), dayjs().endOf('day')]
      },
      {
        label: t('LBL_PRESET_YESTERDAY', 'Yesterday'),
        value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')]
      },
      {
        label: t('LBL_PRESET_LAST_7', 'Last 7 days'),
        value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')]
      },
      {
        label: t('LBL_PRESET_LAST_30', 'Last 30 days'),
        value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')]
      },
      {
        label: t('LBL_PRESET_THIS_MONTH', 'This month'),
        value: [dayjs().startOf('month'), dayjs().endOf('month')]
      },
      {
        label: t('LBL_PRESET_LAST_MONTH', 'Last month'),
        value: [
          dayjs().subtract(1, 'month').startOf('month'),
          dayjs().subtract(1, 'month').endOf('month')
        ]
      }
    ],
    [t]
  )
}

/**
 * FormDatePicker — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export function FormDatePicker(props: DatePickerProps) {
  const { allowClear, ...rest } = props
  return <DatePicker allowClear={allowClear ?? true} {...rest} />
}

/**
 * FormDateRangePicker — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export function FormDateRangePicker(props: RangePickerProps) {
  const { allowClear, presets, ...rest } = props
  const defaultPresets = usePresets()
  return (
    <DatePicker.RangePicker
      allowClear={allowClear ?? true}
      presets={presets ?? defaultPresets}
      {...rest}
    />
  )
}

export default FormDatePicker
